import { countBy, orderBy } from 'lodash-es';
import type { Employee, EmployeeGroup } from '@fc-core';
import type { FilterItem } from '@fc-vehicles/components/shared/vehicle-filter/vehicles-filter.component';
import type { SortBy } from '@fc-shared/ui/sort-by/sort-by';
import { sortByPropertyWithOrder } from '@fc-shared/utils/sort-by-property';

export function searchEmployees(
  employees: Employee[],
  searchText: string,
): Employee[] {
  const search = searchText?.trim().toLocaleLowerCase();
  if (!search) return employees;
  return employees.filter((employee) =>
    [
      employee.primaryPhoneNumber,
      employee.secondaryPhoneNumber,
      employee.fullName,
      employee.email,
    ].some((value) => value?.toLocaleLowerCase().includes(search)),
  );
}

export function filterEmployeesByState(
  employees: Employee[],
  selectedFilters: FilterItem[],
): Employee[] {
  const selectedTypes = new Set(selectedFilters?.map((filter) => filter.type));
  const isArchivedSelected = selectedTypes.has('archived');
  return employees.filter((employee) => {
    const matchesArchived = isArchivedSelected
      ? !!employee.archived
      : !employee.archived;
    const matchesPending =
      !selectedTypes.has('pending') || !!employee.hasInvite;
    const matchesOperator =
      !selectedTypes.has('operator') || !!employee.operator;
    const matchesUser = !selectedTypes.has('user') || !!employee.user;
    const matchesAdmin = !selectedTypes.has('admin') || !!employee.isAdmin;
    return (
      matchesArchived &&
      matchesPending &&
      matchesOperator &&
      matchesUser &&
      matchesAdmin
    );
  });
}

export function filterEmployeesByGroup(
  employees: Employee[],
  selectedGroup: EmployeeGroup,
): Employee[] {
  if (selectedGroup?.id === -1) return employees;
  return employees.filter((employee) =>
    employee.groups?.includes(selectedGroup?.id),
  );
}

export function countEmployeesByGroupId(
  employees: Employee[],
): Record<number, number> {
  return countBy(employees.flatMap((employee) => employee.groups ?? []));
}

export function buildEmployeeGroupListItems(
  groups: EmployeeGroup[],
  searchText: string,
  sortBy: SortBy,
  activeEmployeeCountByGroupId: Record<number, number>,
) {
  const search = searchText?.toLowerCase();
  const matchingGroups = search
    ? groups.filter((group) => group.name?.toLowerCase().includes(search))
    : groups;
  const items = sortByPropertyWithOrder(
    matchingGroups,
    'name',
    sortBy.value === 'name' && !!sortBy.order,
  ).map((group) => ({
    group,
    activeEmployeeCount: activeEmployeeCountByGroupId[group.id] ?? 0,
  }));
  if (sortBy.value !== 'numbers') return items;
  return orderBy(items, 'activeEmployeeCount', sortBy.order ? 'desc' : 'asc');
}
